/**
 * Color utilities for PriceRoute component
 * Generates dynamic color palettes based on client implementation color
 * Similar to ChainVisualization color generation logic
 */

/**
 * Generates a color palette with variations of the base client color
 * for different Elo levels (Elo 1, Elo 2, Elo 3)
 * @param {string} baseColor - User's implementation_color (hex format)
 * @param {number} count - Number of color variations needed (default 3 for Elos)
 * @returns {Array<string>} Array of hex colors for each Elo
 */
export const generateEloColorPalette = (baseColor, count = 3) => {
  // If no base color provided, fall back to a default palette
  if (!baseColor) {
    return [
      "#6366f1", // Elo 1 - Blue-purple
      "#8b5cf6", // Elo 2 - Purple
      "#06b6d4", // Elo 3 - Teal
    ].slice(0, count);
  }

  // Color utility functions
  const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return [h, s, l];
  };

  const hslToHex = (h, s, l) => {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = hue2rgb(p, q, h + 1 / 3);
    const g = hue2rgb(p, q, h);
    const b = hue2rgb(p, q, h - 1 / 3);

    const toHex = (c) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const [baseH, baseS, baseL] = hexToHsl(baseColor);
  const colors = [];

  // Create Elo-specific color variations
  const eloVariations = [
    // Elo 1 - Base color (original client color)
    { h: baseH, s: Math.min(baseS * 0.9, 0.8), l: Math.max(baseL, 0.45) },

    // Elo 2 - Slightly darker and more saturated
    { h: baseH, s: Math.min(baseS * 1.1, 0.9), l: Math.max(baseL - 0.1, 0.35) },

    // Elo 3 - Lighter with subtle hue shift
    {
      h: (baseH + 0.05) % 1,
      s: Math.min(baseS * 0.7, 0.6),
      l: Math.min(baseL + 0.15, 0.7),
    },

    // Additional variations if more colors needed
    // Elo 4+ - Complementary with reduced saturation
    {
      h: (baseH + 0.3) % 1,
      s: Math.min(baseS * 0.6, 0.5),
      l: Math.max(baseL, 0.5),
    },

    // Elo 5+ - Desaturated base
    {
      h: baseH,
      s: Math.min(baseS * 0.4, 0.3),
      l: Math.max(baseL + 0.05, 0.55),
    },
  ];

  // Generate colors based on Elo variations
  for (let i = 0; i < count; i++) {
    const variation = eloVariations[i % eloVariations.length];

    // Ensure colors stay within reasonable bounds
    const finalH = variation.h;
    const finalS = Math.max(0.2, Math.min(0.9, variation.s));
    const finalL = Math.max(0.25, Math.min(0.75, variation.l));

    colors.push(hslToHex(finalH, finalS, finalL));
  }

  return colors;
};

/**
 * Gets a specific Elo color from the palette
 * @param {string} baseColor - User's implementation_color
 * @param {number} eloIndex - Index of the Elo (0 = Elo 1, 1 = Elo 2, 2 = Elo 3)
 * @returns {string} Hex color for the specified Elo
 */
export const getEloColor = (baseColor, eloIndex) => {
  const palette = generateEloColorPalette(baseColor, 3);
  return palette[eloIndex] || palette[0]; // Fallback to first color if index out of bounds
};

/**
 * Generates color variations with opacity for different states
 * @param {string} color - Base hex color
 * @param {number} opacity - Opacity value between 0 and 1
 * @returns {string} RGBA color string
 */
export const addOpacityToColor = (color, opacity = 1) => {
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Creates a lighter version of a color for hover states
 * @param {string} color - Base hex color
 * @param {number} lightenFactor - How much to lighten (0.1 = 10% lighter)
 * @returns {string} Hex color
 */
export const lightenColor = (color, lightenFactor = 0.1) => {
  const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return [h, s, l];
  };

  const hslToHex = (h, s, l) => {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = hue2rgb(p, q, h + 1 / 3);
    const g = hue2rgb(p, q, h);
    const b = hue2rgb(p, q, h - 1 / 3);

    const toHex = (c) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const [h, s, l] = hexToHsl(color);
  const lighterL = Math.min(l + lightenFactor, 1);

  return hslToHex(h, s, lighterL);
};

export default {
  generateEloColorPalette,
  getEloColor,
  addOpacityToColor,
  lightenColor,
};
